// Bepaalt of één conditionele vraag getoond moet worden, gegeven de antwoorden
// die de respondent tot nu toe gaf.
//
// De regel staat als `ShowWhen` op het AFHANKELIJKE veld — niet op de
// antwoordoptie. Een regel op de optie zou breken zodra de bouwer de vragen
// herordent, en is niet leesbaar vanaf de vraag die verdwijnt.
//
// MATCHEN OP ID, NOOIT OP TEKST. Een optie die hernoemd wordt van 'Ja' naar
// 'Jazeker' moet de regel intact laten. Antwoorden dragen hun optie-ID mee als
// `Itemid` en hun matrixkolom als `ColumnID`; die gaan voor. De ruwe `Input`
// telt óók mee als sleutel, want schaal- en nummerantwoorden hebben geen ID —
// daar ís de waarde de identiteit.
// AI-CONTEXT: See docs/pitfalls.md#database-rowtitle-join-strands-renamed-rows
//
// ALLES WAT NIET KLOPT, TOONT. Een onbekende operator, een lege waardenlijst,
// een niet-numerieke drempel: allemaal `true`. Zichtbaarheid is hier
// invulgemak, geen beveiligingspoort — een vraag stil laten verdwijnen is de
// ergere fout. Dat een trigger helemaal niet meer BESTAAT (verwijderd, of op
// dit meetmoment uitgezet) wordt een laag hoger afgevangen, in
// filterFieldsForResponses: daar is de veldenlijst bekend.
//
// AI-CONTEXT: See docs/features/conditional-questions.md

/** Operators die deze versie kent. Al het andere toont. */
const OPERATORS = new Set(['is', 'is-niet', 'gte', 'lte', 'is-ingevuld', 'is-leeg'])

/** Operators waarvoor `Values` gevuld moet zijn om iets te kunnen betekenen. */
const NEEDS_VALUES = new Set(['is', 'is-niet', 'gte', 'lte'])

/**
 * @typedef {object} ShowWhenRule
 * @property {string} FieldID - `QuestionnaireFields.ID` van de triggervraag
 * @property {'is'|'is-niet'|'gte'|'lte'|'is-ingevuld'|'is-leeg'} Operator
 * @property {Array<string|number>} [Values] - optie-ID's, kolom-ID's of één drempelgetal
 * @property {string} [RowID] - `MatrixQuestionRows.ID`, alleen bij een matrixtrigger
 */

/**
 * Heeft dit antwoord een echte waarde? `0` telt mee (een schaalantwoord van 0
 * is een antwoord), een lege of enkel witruimte bevattende tekst niet.
 * @param {object} answer
 * @returns {boolean}
 */
const hasValue = (answer) => {
    const input = answer?.Input
    if (input === undefined || input === null) return false
    if (typeof input === 'number') return Number.isFinite(input)
    return String(input).trim() !== ''
}

/**
 * De identiteiten waarop een antwoord kan matchen: optie-ID, matrixkolom-ID en
 * de ruwe waarde. Botsing tussen een uuid en een antwoordwaarde is in de
 * praktijk uitgesloten.
 * @param {object} answer
 * @returns {Array<string>}
 */
const answerKeys = (answer) => [answer?.Itemid, answer?.ColumnID, answer?.Input]
    .filter((value) => value !== undefined && value !== null && value !== '')
    .map(String)

/**
 * @param {unknown} value
 * @returns {number|null} het getal, of null als het er geen is
 */
const toNumber = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    if (typeof value !== 'string' || value.trim() === '') return null
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

/**
 * @param {ShowWhenRule} [rule] - de regel op het afhankelijke veld; ontbreekt = altijd tonen
 * @param {Array<object>} [responses] - de Responses-context van de respondent
 * @returns {boolean} True = tonen
 */
const evaluateShowWhen = (rule, responses) => {
    if (!rule || !rule.FieldID) return true

    const operator = rule.Operator
    if (!OPERATORS.has(operator)) return true

    const values = Array.isArray(rule.Values) ? rule.Values : []
    if (NEEDS_VALUES.has(operator) && values.length === 0) return true

    const all = Array.isArray(responses) ? responses : []
    // Bij een matrixtrigger telt alleen de genoemde rij: 'rij 1 = Eens' mag niet
    // waar worden doordat rij 2 op 'Eens' staat.
    const answers = all.filter((answer) => (
        answer?.FieldID === rule.FieldID
        && (!rule.RowID || answer?.RowID === rule.RowID)
    ))

    if (operator === 'is-ingevuld') return answers.some(hasValue)
    if (operator === 'is-leeg') return !answers.some(hasValue)

    if (operator === 'gte' || operator === 'lte') {
        const threshold = toNumber(values[0])
        if (threshold === null) return true // onbruikbare drempel → tonen
        return answers.some((answer) => {
            const given = toNumber(answer?.Input)
            if (given === null) return false
            return operator === 'gte' ? given >= threshold : given <= threshold
        })
    }

    const allowed = new Set(values.map(String))
    const matches = answers.some((answer) => answerKeys(answer).some((key) => allowed.has(key)))

    // 'is' is onwaar zolang er geen passend antwoord is; 'is-niet' is dan waar.
    // Dat is gewone logica en bewust niet gladgestreken: wie niets antwoordde,
    // antwoordde ook geen 'Ja'.
    return operator === 'is' ? matches : !matches
}

export default evaluateShowWhen
